import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import openaiService from '../../../utils/openaiService';

const AIAnalysisPanel = ({ performanceData }) => {
  const [analysis, setAnalysis] = useState(null);
  const [quickInsights, setQuickInsights] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isGeneratingInsights, setIsGeneratingInsights] = useState(false);
  const [error, setError] = useState(null);
  const [lastAnalysisTime, setLastAnalysisTime] = useState(null);
  const [quotaStatus, setQuotaStatus] = useState({ quotaExceeded: false, canRetry: true });

  // Sample KPI data structure for testing
  const mockKPIData = {
    avgCPA: 42.75,
    avgCallLength: 4.2,
    winRate: 33.9,
    totalBids: 1247,
    wonBids: 423,
    totalSpent: 18084.25,
    qualityScore: 8.7,
    statePerformance: [
      { state: 'CA', winRate: 38.2, cpa: 39.50, avgCallLength: 4.8 },
      { state: 'TX', winRate: 31.5, cpa: 44.25, avgCallLength: 3.9 },
      { state: 'FL', winRate: 35.8, cpa: 41.10, avgCallLength: 4.5 },
      { state: 'NY', winRate: 29.3, cpa: 48.75, avgCallLength: 3.2 },
      { state: 'IL', winRate: 33.1, cpa: 43.80, avgCallLength: 4.1 }
    ],
    leadTypes: [
      { type: 'Auto Insurance', count: 245, avgCPA: 38.50 },
      { type: 'Home Insurance', count: 156, avgCPA: 47.25 },
      { type: 'Life Insurance', count: 22, avgCPA: 52.80 }
    ]
  };

  const handleFullAnalysis = async () => {
    setIsAnalyzing(true);
    setError(null);
    
    try {
      const kpiData = performanceData || mockKPIData;
      const result = await openaiService?.analyzeKPIData(kpiData);
      setAnalysis(result);
      setLastAnalysisTime(new Date());
      
      // Update quota status
      setQuotaStatus(openaiService?.getQuotaStatus());
    } catch (err) {
      setError(err?.message || 'Failed to generate analysis');
      setQuotaStatus(openaiService?.getQuotaStatus());
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleQuickInsights = async () => {
    setIsGeneratingInsights(true);
    setError(null);
    
    try {
      const realtimeData = {
        winRate: performanceData?.winRate || mockKPIData?.winRate,
        avgCPA: performanceData?.avgCPA || mockKPIData?.avgCPA,
        qualityScore: performanceData?.qualityScore || mockKPIData?.qualityScore,
        activeBids: performanceData?.activeBids || 8
      };
      
      const result = await openaiService?.generateQuickInsights(realtimeData);
      setQuickInsights(result);
      
      // Update quota status
      setQuotaStatus(openaiService?.getQuotaStatus());
    } catch (err) {
      setError(err?.message || 'Failed to generate insights');
      setQuotaStatus(openaiService?.getQuotaStatus());
    } finally {
      setIsGeneratingInsights(false);
    }
  };

  const handleRetryAfterQuota = () => {
    openaiService?.resetQuotaStatus();
    setQuotaStatus({ quotaExceeded: false, canRetry: true });
    setError(null);
  };

  // Auto-generate quick insights on component mount, but handle quota gracefully
  useEffect(() => {
    const loadInitialInsights = async () => {
      try {
        await handleQuickInsights();
      } catch (err) {
        // Silently handle initial load errors - user can manually retry
        console.warn('Initial insights load failed:', err?.message);
      }
    };
    
    loadInitialInsights();
  }, []);

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high': return 'text-error bg-error/10 border-error/20';
      case 'medium': return 'text-warning bg-warning/10 border-warning/20';
      case 'low': return 'text-success bg-success/10 border-success/20';
      default: return 'text-muted-foreground bg-muted/10 border-border';
    }
  };

  const renderFallbackBadge = (isFallback) => {
    if (!isFallback) return null;
    
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
        <Icon name="AlertTriangle" size={12} className="mr-1" />
        Offline Mode
      </span>
    );
  };

  return (
    <div className="h-full flex flex-col bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="Brain" size={20} className="text-accent" />
          <h2 className="text-lg font-semibold text-foreground">AI Analysis & Recommendations</h2>
          {quotaStatus?.quotaExceeded && (
            <Icon name="AlertCircle" size={16} className="text-warning" title="API quota exceeded - using fallback analysis" />
          )}
        </div>
        <div className="flex items-center space-x-2">
          {quotaStatus?.quotaExceeded && quotaStatus?.canRetry && (
            <Button
              variant="ghost"
              size="sm"
              iconName="RotateCcw"
              onClick={handleRetryAfterQuota}
              title="Reset quota status and retry"
            >
              Retry API
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            iconName="Zap"
            onClick={handleQuickInsights}
            disabled={isGeneratingInsights}
          >
            {isGeneratingInsights ? 'Analyzing...' : 'Quick Insights'}
          </Button>
          <Button
            variant="default"
            size="sm"
            iconName="BarChart3"
            onClick={handleFullAnalysis}
            disabled={isAnalyzing}
          >
            {isAnalyzing ? 'Analyzing...' : 'Full Analysis'}
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {error && (
          <div className="p-3 bg-error/10 border border-error/20 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="AlertCircle" size={16} className="text-error" />
              <span className="text-sm font-medium text-error">API Error</span>
              <Button variant="ghost" size="sm" onClick={() => setError(null)}>
                <Icon name="X" size={14} />
              </Button>
            </div>
            <p className="text-xs text-error mb-2">{error}</p>
            {error?.includes('quota exceeded') && (
              <div className="text-xs text-muted-foreground">
                <p className="mb-1">• Fallback analysis is being used instead</p>
                <p className="mb-1">• Check your OpenAI billing plan and usage</p>
                <p>• Analysis will automatically retry after 1 hour</p>
              </div>
            )}
          </div>
        )}

        {/* Quick Insights Section */}
        {quickInsights && (
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-foreground flex items-center">
                <Icon name="Zap" size={16} className="mr-2 text-accent" />
                Quick Insights
                <span className="ml-2 text-xs text-muted-foreground">Real-time</span>
              </h3>
              {renderFallbackBadge(quickInsights?.isFallback)}
            </div>
            
            <div className="mb-3 text-xs text-muted-foreground">
              Status: <span className="font-medium">{quickInsights?.overallStatus}</span>
            </div>

            <div className="space-y-2">
              {quickInsights?.insights?.map((insight, index) => (
                <div key={index} className="flex items-start space-x-3 p-3 bg-card rounded border border-border">
                  <div className={`px-2 py-1 rounded text-xs font-medium border ${getPriorityColor(insight?.priority)}`}>
                    {insight?.priority?.toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-foreground mb-1">{insight?.title}</h4>
                    <p className="text-xs text-muted-foreground">{insight?.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Full Analysis Section */}
        {analysis && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground flex items-center">
                <Icon name="BarChart3" size={16} className="mr-2 text-accent" />
                Comprehensive Analysis
              </h3>
              <div className="flex items-center space-x-2">
                {renderFallbackBadge(analysis?.isFallback)}
                <div className="text-xs text-muted-foreground">
                  {lastAnalysisTime && `Generated ${lastAnalysisTime?.toLocaleTimeString()}`}
                </div>
              </div>
            </div>

            {/* Overall Assessment */}
            <div className="bg-muted/30 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center">
                <Icon name="Target" size={14} className="mr-1" />
                Overall Assessment
                <div className="ml-auto flex items-center space-x-1">
                  <span className="text-xs text-muted-foreground">Confidence:</span>
                  <span className="text-xs font-medium text-accent">{(analysis?.confidenceScore * 100)?.toFixed(0)}%</span>
                </div>
              </h4>
              <p className="text-sm text-muted-foreground">{analysis?.overallAssessment}</p>
            </div>

            {/* Top States Recommendations */}
            <div className="bg-muted/30 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center">
                <Icon name="MapPin" size={14} className="mr-1" />
                Top States to Focus On
              </h4>
              <div className="space-y-3">
                {analysis?.topStates?.slice(0, 3)?.map((state, index) => (
                  <div key={index} className="p-3 bg-card rounded border border-border">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="text-sm font-medium text-foreground">{state?.state}</h5>
                      <span className="text-xs bg-accent/10 text-accent px-2 py-1 rounded">
                        {state?.recommendedBidAdjustment}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">{state?.reason}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* CPA Optimization */}
            <div className="bg-muted/30 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center">
                <Icon name="DollarSign" size={14} className="mr-1" />
                CPA Optimization Recommendations
              </h4>
              <ul className="space-y-2">
                {analysis?.cpaOptimization?.map((recommendation, index) => (
                  <li key={index} className="text-xs text-muted-foreground flex items-start">
                    <Icon name="ArrowRight" size={12} className="mr-2 mt-0.5 text-accent" />
                    {recommendation}
                  </li>
                ))}
              </ul>
            </div>

            {/* Call Quality Insights */}
            <div className="bg-muted/30 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center">
                <Icon name="Phone" size={14} className="mr-1" />
                Call Quality Insights
              </h4>
              <ul className="space-y-2">
                {analysis?.callQualityInsights?.map((insight, index) => (
                  <li key={index} className="text-xs text-muted-foreground flex items-start">
                    <Icon name="ArrowRight" size={12} className="mr-2 mt-0.5 text-accent" />
                    {insight}
                  </li>
                ))}
              </ul>
            </div>

            {/* Bidding Strategy Adjustments */}
            <div className="bg-muted/30 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center">
                <Icon name="Settings" size={14} className="mr-1" />
                Bidding Strategy Adjustments
              </h4>
              <ul className="space-y-2">
                {analysis?.biddingStrategyAdjustments?.map((adjustment, index) => (
                  <li key={index} className="text-xs text-muted-foreground flex items-start">
                    <Icon name="ArrowRight" size={12} className="mr-2 mt-0.5 text-accent" />
                    {adjustment}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Loading States */}
        {(isAnalyzing || isGeneratingInsights) && (
          <div className="flex items-center justify-center p-8">
            <div className="flex items-center space-x-3">
              <div className="animate-spin">
                <Icon name="Brain" size={20} className="text-accent" />
              </div>
              <span className="text-sm text-muted-foreground">
                {isAnalyzing ? 'Running comprehensive analysis...' : 'Generating quick insights...'}
              </span>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!analysis && !quickInsights && !isAnalyzing && !isGeneratingInsights && !error && (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <Icon name="Brain" size={48} className="text-muted-foreground mb-4" />
            <h3 className="text-sm font-medium text-foreground mb-2">AI-Powered Bidding Analysis</h3>
            <p className="text-xs text-muted-foreground mb-4 max-w-sm">
              Get intelligent recommendations on optimal states to bid on based on your KPIs like cost per acquisition and call length.
            </p>
            {quotaStatus?.quotaExceeded && (
              <div className="mb-4 p-3 bg-warning/10 border border-warning/20 rounded-lg">
                <p className="text-xs text-warning font-medium mb-1">OpenAI API Quota Exceeded</p>
                <p className="text-xs text-muted-foreground">Fallback analysis will be used. Check your billing plan for full AI features.</p>
              </div>
            )}
            <div className="flex space-x-2">
              <Button variant="outline" size="sm" onClick={handleQuickInsights}>
                Quick Insights
              </Button>
              <Button variant="default" size="sm" onClick={handleFullAnalysis}>
                Full Analysis
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIAnalysisPanel;